import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { conditionMultiplierFor, sizeMultiplierFor } from '@shared/constants/index.ts';
import {
  COMMERCIAL_PRESET_IDS,
  commercialPreset,
  presetLabel,
  presetQuoteFields,
  type CommercialPresetId,
  type CommercialPresetRoom,
} from '@shared/quotes/commercial-presets.ts';
import { defaultGroupOf } from '@shared/quotes/groups.ts';
import type { Quote, QuoteRoom } from '@shared/types/index.ts';
import { temporaryId } from '../../hooks/quote-merge.ts';
import { ConfirmDialog } from '../ui.tsx';

/**
 * Ready-made starting points for a business quote.
 *
 * Picking one fills rooms, tasks, how often, and the agreement. Anything
 * already on the quote is replaced only after a yes.
 */
export function CommercialPresets({
  quote,
  update,
  readOnly,
}: {
  quote: Quote;
  update: (mutate: (draft: Quote) => Quote) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<CommercialPresetId | null>(null);
  if (quote.segment !== 'commercial' || readOnly) return null;

  function apply(id: CommercialPresetId) {
    const preset = commercialPreset(id);
    update((draft) => {
      const groupId = defaultGroupOf(draft.groups ?? [])?.id ?? null;
      return {
        ...draft,
        ...presetQuoteFields(preset),
        rooms: preset.rooms.map((room, index) => roomFromPreset(room, draft.language, groupId, index)),
      };
    });
    setPending(null);
  }

  function choose(id: CommercialPresetId) {
    if (quote.rooms.length > 0) {
      setPending(id);
      return;
    }
    apply(id);
  }

  return (
    <section className="card mb-6" aria-labelledby="commercial-presets-heading">
      <h2 id="commercial-presets-heading" className="text-lg font-semibold">
        {t('commercialPresets.heading')}
      </h2>
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('commercialPresets.hint')}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {COMMERCIAL_PRESET_IDS.map((id) => (
          <button
            key={id}
            type="button"
            className="min-h-11 rounded-md border border-[color:var(--color-line)] px-3 py-2 text-left hover:border-[color:var(--color-brand)]"
            onClick={() => choose(id)}
          >
            <span className="block font-medium">{t(`commercialPresets.${id}`)}</span>
            <span className="block text-sm text-[color:var(--color-muted)]">{t(`commercialPresets.${id}Hint`)}</span>
          </button>
        ))}
      </div>
      {pending ? (
        <ConfirmDialog
          title={t('commercialPresets.replaceTitle')}
          confirmLabel={t('commercialPresets.replaceAction')}
          onCancel={() => setPending(null)}
          onConfirm={() => apply(pending)}
        >
          <p>{t('commercialPresets.replaceBody')}</p>
        </ConfirmDialog>
      ) : null}
    </section>
  );
}

function roomFromPreset(
  room: CommercialPresetRoom,
  language: string,
  groupId: string | null,
  displayOrder: number,
): QuoteRoom {
  const other = language === 'es' ? { en: room.name.en } : { es: room.name.es };
  return {
    id: temporaryId(),
    roomTemplateId: null,
    roomName: presetLabel(room.name, language),
    roomNameTranslations: other,
    quantity: 1,
    sizeCode: 'medium',
    sizeMultiplierBasisPoints: sizeMultiplierFor('medium'),
    conditionCode: room.condition,
    conditionMultiplierBasisPoints: conditionMultiplierFor(room.condition),
    baseMinutes: 0,
    calculatedMinutes: 0,
    adjustedMinutes: null,
    billableMinutes: 0,
    isManuallyAdjusted: false,
    flatPriceCents: null,
    chargeCents: null,
    measuredSquareFeet: room.squareFeet,
    floorType: room.floorType,
    customerNotes: null,
    internalNotes: null,
    displayOrder,
    groupId,
    services: room.tasks.map((task, index) => ({
      id: temporaryId(),
      serviceTemplateId: null,
      name: presetLabel(task.name, language),
      description: null,
      nameTranslations: language === 'es' ? { en: task.name.en } : { es: task.name.es },
      descriptionTranslations: null,
      estimatedMinutes: task.minutes,
      isCustom: true,
      isSelected: true,
      showOnCustomerQuote: true,
      isTaxable: true,
      frequency: task.frequency,
      measurementBasis: 'time' as const,
      measuredQuantity: null,
      productionRatePerHour: null,
      customerNotes: null,
      internalNotes: null,
      displayOrder: index,
    })),
  };
}
